import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  Progress,
  Avatar,
  Tooltip,
  Badge,
  Divider,
} from "antd";
import {
  FaProjectDiagram,
  FaSearch,
  FaEye,
  FaCheckCircle,
  FaPauseCircle,
  FaExclamationTriangle,
  FaEdit,
  FaPlus,
  FaPlayCircle,
  FaClock,
  FaFilter,
  FaCalendarAlt,
  FaUsers,
  FaFlag,
  FaLightbulb,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaTrash,
  FaBullseye,
  FaUserTag,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import Api from "../api/Api";
import dayjs from "dayjs";
import "./Projeto.css";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return str;
  }
};

const getStatusInfo = (id_status) => {
  const map = {
    1: { label: "ABERTO", color: "blue", icon: <FaPlayCircle /> },
    2: { label: "FECHADO", color: "green", icon: <FaCheckCircle /> },
    3: { label: "SUSPENSO", color: "orange", icon: <FaPauseCircle /> },
    4: { label: "EM ANÁLISE", color: "purple", icon: <FaClock /> },
  };
  return map[id_status] || { label: "DESCONHECIDO", color: "default", icon: null };
};

const getCriticidadeInfo = (id_criticidade) => {
  const map = {
    1: { label: "BAIXA", color: "success", icon: <FaArrowDown />, bgColor: "#f6ffed", textColor: "#52c41a" },
    2: { label: "MÉDIA", color: "warning", icon: <FaMinus />, bgColor: "#fffbe6", textColor: "#faad14" },
    3: { label: "ALTA", color: "error", icon: <FaArrowUp />, bgColor: "#fff1f0", textColor: "#ff4d4f" },
  };
  return map[id_criticidade] || { label: "—", color: "default", icon: null, bgColor: "#f5f5f5", textColor: "#8c8c8c" };
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE ADICIONAR/EDITAR PROJETO
───────────────────────────────────────────────────────────────────────────── */
const ProjetoFormModal = ({
  open,
  onClose,
  onSuccess,
  editingProjeto,
  userStored,
  criticidadeList,
  objectivoList,
  matrizList,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Lista de responsáveis com matriz — cada item: { id_user, id_matriz }
  const [responsaveis, setResponsaveis] = useState([{ id_user: null, id_matriz: null }]);

  useEffect(() => {
    if (open) {
      loadUsuarios();

      if (editingProjeto) {
        form.setFieldsValue({
          titulo: editingProjeto.titulo,
          descricao: editingProjeto.descricao,
          inicio: editingProjeto.inicio ? dayjs(editingProjeto.inicio) : null,
          fim: editingProjeto.fim ? dayjs(editingProjeto.fim) : null,
          id_criticidade: editingProjeto.id_criticidade,
          id_objectivo: editingProjeto.id_objectivo,
        });

        // Preenche responsáveis existentes
        if (Array.isArray(editingProjeto.responsaveis) && editingProjeto.responsaveis.length > 0) {
          setResponsaveis(editingProjeto.responsaveis.map((r) => ({
            id_user: r.id_user,
            id_matriz: r.id_matriz,
          })));
        } else {
          setResponsaveis([{ id_user: null, id_matriz: null }]);
        }
      } else {
        form.resetFields();
        setResponsaveis([{ id_user: null, id_matriz: null }]);
      }
    }
  }, [open, editingProjeto, form]);

  const loadUsuarios = async () => {
    try {
      setLoadingUsers(true);
      const payload = { id_direcao: userStored.direcao };
      const response = await Api.ListUserDirecao(payload);
      setUsuarios(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar usuários.");
      setUsuarios([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const addResponsavel = () => {
    setResponsaveis((prev) => [...prev, { id_user: null, id_matriz: null }]);
  };

  const removeResponsavel = (index) => {
    setResponsaveis((prev) => prev.filter((_, i) => i !== index));
  };

  const updateResponsavel = (index, field, value) => {
    setResponsaveis((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Validar responsáveis
      const responsaveisValidos = responsaveis.filter(
        (r) => r.id_user && r.id_matriz
      );
      if (responsaveisValidos.length === 0) {
        toast.error("Adicione pelo menos um responsável com matriz definida.");
        return;
      }
      const responsaveisIncompletos = responsaveis.some(
        (r) => (r.id_user && !r.id_matriz) || (!r.id_user && r.id_matriz)
      );
      if (responsaveisIncompletos) {
        toast.error("Preencha o utilizador e a matriz de todos os responsáveis.");
        return;
      }

      setLoading(true);

      const payload = {
        titulo: values.titulo,
        descricao: values.descricao,
        inicio: values.inicio ? values.inicio.format("YYYY-MM-DD") : null,
        fim: values.fim ? values.fim.format("YYYY-MM-DD") : null,
        id_criticidade: values.id_criticidade,
        id_direcao: userStored.direcao,
        id_objectivo: values.id_objectivo,
        responsaveis: responsaveisValidos,
      };

      if (editingProjeto) {
        await Api.AlterProjeto({
          ...payload,
          id: editingProjeto.id,
          id_status: editingProjeto.id_status,
        });
        toast.success("Projeto atualizado com sucesso!");
      } else {
        await Api.AddProjeto(payload);
        if (userStored.diretor === "N") {
          toast.success("O seu projeto precisa de aprovação do director!");
        } else {
          toast.success("Projeto criado com sucesso!");
        }
      }

      form.resetFields();
      setResponsaveis([{ id_user: null, id_matriz: null }]);
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Preencha todos os campos obrigatórios.");
      } else {
        toast.error(
          editingProjeto ? "Erro ao atualizar projeto." : "Erro ao criar projeto."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Utilizadores já selecionados para evitar duplicatas
  const selectedUsers = responsaveis.map((r) => r.id_user).filter(Boolean);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={760}
      title={
        <Space>
          <FaProjectDiagram style={{ color: "#1677ff", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>
            {editingProjeto ? "Editar Projeto" : "Novo Projeto"}
          </span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          {editingProjeto ? "Atualizar" : "Criar Projeto"}
        </Button>,
      ]}
      bodyStyle={{ padding: "24px 24px 0" }}
    >
      <Form form={form} layout="vertical">
        {/* Título */}
        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Título</span>}
          name="titulo"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <Input placeholder="Ex: Desenvolvimento do Portal Interno" size="large" />
        </Form.Item>

        {/* Descrição */}
        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Descrição</span>}
          name="descricao"
        >
          <TextArea rows={3} placeholder="Detalhes do projeto..." />
        </Form.Item>

        {/* Objectivo e Criticidade */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Objectivo</span>}
              name="id_objectivo"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <Select
                size="large"
                placeholder="Selecione o objectivo"
                showSearch
                optionFilterProp="children"
              >
                {objectivoList.map((obj) => (
                  <Option key={obj.id} value={obj.id}>
                    <Space>
                      <FaBullseye style={{ color: "#1677ff" }} />
                      {obj.titulo ?? obj.descricao ?? obj.nome}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Criticidade</span>}
              name="id_criticidade"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <Select size="large" placeholder="Selecione a criticidade">
                {criticidadeList.map((crit) => {
                  const info = getCriticidadeInfo(crit.id);
                  return (
                    <Option key={crit.id} value={crit.id}>
                      <Space>
                        <span style={{ color: info.textColor }}>{info.icon}</span>
                        {crit.descricao}
                      </Space>
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Datas */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data de Início</span>}
              name="inicio"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data de Fim</span>}
              name="fim"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Responsáveis com Matriz RACI */}
        <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600, color: "#1f1f1f" }}>
          <Space>
            <FaUsers style={{ color: "#1677ff" }} />
            Responsáveis e Matriz RACI
          </Space>
        </Divider>

        <div style={{ marginBottom: 16 }}>
          {responsaveis.map((resp, index) => (
            <Row
              key={index}
              gutter={12}
              align="middle"
              style={{
                marginBottom: 10,
                background: "#fafafa",
                borderRadius: 8,
                padding: "10px 12px",
                border: "1px solid #f0f0f0",
              }}
            >
              {/* Utilizador */}
              <Col flex="auto">
                <Select
                  style={{ width: "100%" }}
                  size="middle"
                  placeholder="Selecione o utilizador"
                  showSearch
                  loading={loadingUsers}
                  value={resp.id_user || undefined}
                  onChange={(val) => updateResponsavel(index, "id_user", val)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {usuarios.map((user) => (
                    <Option
                      key={user.id}
                      value={user.id}
                      disabled={
                        selectedUsers.includes(user.id) && resp.id_user !== user.id
                      }
                    >
                      {user.nome}
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* Matriz RACI */}
              <Col style={{ width: 200 }}>
                <Select
                  style={{ width: "100%" }}
                  size="middle"
                  placeholder="Papel (RACI)"
                  value={resp.id_matriz || undefined}
                  onChange={(val) => updateResponsavel(index, "id_matriz", val)}
                >
                  {matrizList.map((m) => (
                    <Option key={m.id} value={m.id}>
                      <Space>
                        <Tag
                          color="blue"
                          style={{
                            minWidth: 28,
                            textAlign: "center",
                            fontWeight: 700,
                            borderRadius: 4,
                            padding: "0 6px",
                          }}
                        >
                          {m.abv}
                        </Tag>
                        <span style={{ fontSize: 12 }}>
                          {m.descricao.split("/")[0].trim()}
                        </span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* Remover */}
              <Col>
                <Tooltip title="Remover responsável">
                  <Button
                    type="text"
                    danger
                    icon={<FaTrash />}
                    size="small"
                    disabled={responsaveis.length === 1}
                    onClick={() => removeResponsavel(index)}
                  />
                </Tooltip>
              </Col>
            </Row>
          ))}

          <Button
            type="dashed"
            icon={<FaPlus />}
            onClick={addResponsavel}
            style={{ width: "100%", borderRadius: 8, marginTop: 4 }}
          >
            Adicionar Responsável
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   PROJETO PAGE
───────────────────────────────────────────────────────────────────────────── */
const Projeto = () => {
  const [projetos, setProjetos] = useState([]);
  const [criticidadeList, setCriticidadeList] = useState([]);
  const [objectivoList, setObjectivoList] = useState([]);
  const [matrizList, setMatrizList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("TODOS");
  const [criticidadeFilter, setCriticidadeFilter] = useState("TODAS");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingProjeto, setEditingProjeto] = useState(null);

  const navigate = useNavigate();
  const userStored = JSON.parse(localStorage.getItem("user")) || {};
  const canCreateProject =
  userStored?.perfil === "ADMINISTRACAO" ||
  userStored?.perfil === "DIRETOR";

  useEffect(() => {
    document.title = "Gestão de Projetos | Dashboard";
  }, []);

  const loadCriticidade = useCallback(async () => {
    try {
      const response = await Api.ListCriticidade();
      setCriticidadeList(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar criticidades.");
      setCriticidadeList([]);
    }
  }, []);

  const loadObjectivo = useCallback(async () => {
    try {
      const response = await Api.ListObjetivo({direcao:userStored.direcao});
      setObjectivoList(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar objectivos.");
      setObjectivoList([]);
    }
  }, []);

  const loadMatriz = useCallback(async () => {
    try {
      const response = await Api.ListMatriz();
      setMatrizList(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar matriz RACI.");
      setMatrizList([]);
    }
  }, []);

  const loadProjetos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListProjeto({ id_direcao: userStored.direcao });
      const list = Array.isArray(response) ? response : [];

      // Carrega responsáveis de cada projeto
      const projetosComResponsaveis = await Promise.all(
        list.map(async (projeto) => {
          try {
            const responsaveis = await Api.ListResponsavelProjeto({ id: projeto.id });
            return {
              ...projeto,
              responsaveisNomes: Array.isArray(responsaveis) ? responsaveis : [],
              // Formato esperado pelo modal de edição
              responsaveis: Array.isArray(responsaveis)
                ? responsaveis.map((r) => ({
                    id_user: r.id_user ?? r.id,
                    id_matriz: r.id_matriz,
                  }))
                : [],
            };
          } catch {
            return { ...projeto, responsaveisNomes: [], responsaveis: [] };
          }
        })
      );

      setProjetos(projetosComResponsaveis);
    } catch {
      toast.error("Erro ao carregar projetos.");
      setProjetos([]);
    } finally {
      setLoading(false);
    }
  }, [userStored.direcao]);

  useEffect(() => {
    loadCriticidade();
    loadObjectivo();
    loadMatriz();
    loadProjetos();
  }, [loadCriticidade, loadObjectivo, loadMatriz, loadProjetos]);

  const canEditProject = (projeto) => {
    if (!projeto) return false;
        if (projeto.id_status === 2) return false;
    if(  userStored?.perfil === "ADMINISTRACAO" )return true;
    if (userStored.diretor !== "S") return false;

    return true;
  };

  const openFormModal = (projeto = null) => {
    if (projeto && !canEditProject(projeto)) {
      if (userStored.diretor !== "S") {
        toast.warning("Apenas diretores podem editar projetos.");
      } else if (projeto.id_status === 2) {
        toast.warning("Projetos fechados não podem ser editados.");
      }
      return;
    }
    setEditingProjeto(projeto);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingProjeto(null);
  };

  const openDetalhes = (projeto) => {
    navigate(`/projectos/${projeto.id}`, { state: { projeto } });
  };

  const handleFormSuccess = () => {
    loadProjetos();
  };

  const countAberto = projetos.filter((p) => p.id_status === 1).length;
  const countFechado = projetos.filter((p) => p.id_status === 2).length;
  const countSuspenso = projetos.filter((p) => p.id_status === 3).length;
  const countCriticidadeAlta = projetos.filter((p) => p.id_criticidade === 3).length;

  const filtered = projetos.filter((p) => {
    const statusInfo = getStatusInfo(p.id_status);
    const matchFilter = filter === "TODOS" || statusInfo.label === filter;

    const criticidadeInfo = getCriticidadeInfo(p.id_criticidade);
    const matchCriticidade =
      criticidadeFilter === "TODAS" || criticidadeInfo.label === criticidadeFilter;

    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      p.titulo?.toLowerCase().includes(q) ||
      p.descricao?.toLowerCase().includes(q);

    return matchFilter && matchCriticidade && matchSearch;
  });

  const columns = [
    {
      title: "Projeto",
      dataIndex: "titulo",
      key: "titulo",
      width: 280,
      render: (titulo, record) => {
        const objectivo = objectivoList.find((o) => o.id === record.id_objectivo);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar
              shape="square"
              size="large"
              style={{
                backgroundColor: "#e6f4ff",
                color: "#1677ff",
                borderRadius: 8,
                flexShrink: 0,
              }}
              icon={<FaProjectDiagram />}
            />
            <div>
              <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 4 }}>
                {titulo}
              </div>
              {objectivo && (
                <Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <FaBullseye />
                  {objectivo.titulo ?? objectivo.descricao ?? objectivo.nome}
                </Text>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Criticidade",
      dataIndex: "id_criticidade",
      key: "criticidade",
      width: 120,
      render: (id_criticidade) => {
        const info = getCriticidadeInfo(id_criticidade);
        return (
          <Tag
            color={info.color}
            icon={info.icon}
            style={{
              borderRadius: 20,
              padding: "4px 12px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {info.label}
          </Tag>
        );
      },
    },
    {
      title: "Período",
      key: "periodo",
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.inicio)}
          </Text>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.fim)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Responsáveis",
      key: "responsaveis",
      width: 180,
      render: (_, record) => {
        const lista = record.responsaveisNomes || [];
        if (lista.length === 0)
          return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;

        return (
          <Avatar.Group maxCount={3} size="small">
            {lista.map((r, i) => {
              const matrizItem = matrizList.find(
                (m) => m.id === (r.id_matriz ?? r.pivot?.id_matriz)
              );
              const nome = r.nome ?? r.name ?? `User ${r.id_user ?? r.id}`;
              const tooltip = matrizItem
                ? `${nome} (${matrizItem.abv})`
                : nome;
              return (
                <Tooltip key={i} title={tooltip}>
                  <Avatar
                    size="small"
                    style={{
                      backgroundColor: "#1677ff",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {nome.charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              );
            })}
          </Avatar.Group>
        );
      },
    },
    {
      title: "Progresso",
      dataIndex: "progresso",
      key: "progresso",
      width: 180,
      render: (progresso) => {
        const valor = parseFloat(progresso) || 0;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Progress
              percent={valor}
              size="small"
              status={valor === 100 ? "success" : "active"}
              style={{ flex: 1 }}
              strokeColor={{ "0%": "#1677ff", "100%": "#52c41a" }}
            />
            <Text strong style={{ minWidth: 45, textAlign: "right" }}>
              {valor}%
            </Text>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "id_status",
      key: "status",
      width: 130,
      render: (id_status) => {
        const { label, color, icon } = getStatusInfo(id_status);
        return (
          <Tag
            color={color}
            style={{
              borderRadius: 20,
              padding: "4px 12px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {icon} {label}
          </Tag>
        );
      },
    },
   
    {
      title: "Ações",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          {canEditProject(record) && (
            <Tooltip title="Editar projeto">
              <Button
                type="text"
                icon={<FaEdit />}
                onClick={() => openFormModal(record)}
                style={{ color: "#1677ff" }}
              />
            </Tooltip>
          )}
          <Tooltip title="Ver detalhes">
            <Button
              type="text"
              icon={<FaEye />}
              onClick={() => openDetalhes(record)}
              style={{ color: "#52c41a" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="projeto-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaProjectDiagram style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Gestão de Projetos
                  </Title>
            
                </div>
              </Space>
            </Col>
{canCreateProject && (
  <Col>
    <Button
      type="primary"
      size="large"
      icon={<FaPlus />}
      onClick={() => openFormModal()}
      style={{
        backgroundColor: "#fff",
        color: "#1677ff",
        border: "none",
        fontWeight: 600,
        borderRadius: 30,
        padding: "0 24px",
        height: 48,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      Novo Projeto
    </Button>
  </Col>
)}
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          {[
            {
              title: "Total de Projetos",
              value: projetos.length,
              color: "#1677ff",
              bgColor: "#e6f4ff",
              icon: <FaProjectDiagram style={{ fontSize: 24, color: "#1677ff" }} />,
            },
            {
              title: "Projetos Abertos",
              value: countAberto,
              color: "#1677ff",
              bgColor: "#e6f4ff",
              icon: <FaPlayCircle style={{ fontSize: 24, color: "#1677ff" }} />,
            },
            {
              title: "Criticidade Alta",
              value: countCriticidadeAlta,
              color: "#ff4d4f",
              bgColor: "#fff1f0",
              icon: <FaExclamationTriangle style={{ fontSize: 24, color: "#ff4d4f" }} />,
            },
            {
              title: "Suspensos",
              value: countSuspenso,
              color: "#faad14",
              bgColor: "#fffbe6",
              icon: <FaPauseCircle style={{ fontSize: 24, color: "#faad14" }} />,
            },
          ].map((stat, i) => (
            <Col xs={24} sm={12} lg={6} key={i}>
              <Card
                className="stat-card"
                bordered={false}
                style={{
                  borderRadius: 16,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <Statistic
                  title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>{stat.title}</span>}
                  value={stat.value}
                  valueStyle={{ fontSize: 32, fontWeight: 700, color: stat.color }}
                  prefix={
                    <div
                      style={{
                        backgroundColor: stat.bgColor,
                        borderRadius: 12,
                        padding: 10,
                        marginRight: 16,
                      }}
                    >
                      {stat.icon}
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barra de ferramentas */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por título ou descrição..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>
            <Col>
              <Select
                size="large"
                style={{ width: 165, borderRadius: 30 }}
                value={filter}
                onChange={setFilter}
                suffixIcon={<FaFilter style={{ color: "#1677ff" }} />}
              >
                <Option value="TODOS">Todos os status</Option>
                <Option value="ABERTO">Abertos</Option>
                <Option value="EM ANÁLISE">Em Análise</Option>
                <Option value="SUSPENSO">Suspensos</Option>
                <Option value="FECHADO">Fechados</Option>
              </Select>
            </Col>
            <Col>
              <Select
                size="large"
                style={{ width: 170, borderRadius: 30 }}
                value={criticidadeFilter}
                onChange={setCriticidadeFilter}
                suffixIcon={<FaFlag style={{ color: "#ff4d4f" }} />}
              >
                <Option value="TODAS">Todas criticidades</Option>
                <Option value="BAIXA">Baixa</Option>
                <Option value="MÉDIA">Média</Option>
                <Option value="ALTA">Alta</Option>
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Tabela de projetos */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} projetos`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhum projeto encontrado" }}
            scroll={{ x: 1300 }}
          />
        </Card>

        {/* Modal de formulário */}
        <ProjetoFormModal
          open={formModalOpen}
          onClose={closeFormModal}
          onSuccess={handleFormSuccess}
          editingProjeto={editingProjeto}
          userStored={userStored}
          criticidadeList={criticidadeList}
          objectivoList={objectivoList}
          matrizList={matrizList}
        />
      </Content>
    </Layout>
  );
};

export default Projeto;